import React from 'react';
import { Link } from 'react-router-dom';
import { Info, ShieldCheck, Palette, Globe, Sparkles, KeyRound } from 'lucide-react';
import ContainerPageLayout, { CodeBlock, ReqBadge, DownloadButtons } from '../components/ContainerPageLayout';
import ApiPlayground from '../components/ApiPlayground';
import LangTabs from '../components/LangTabs';

const TOC = [
  { id: 'overview', label: 'Overview' },
  { id: 'outcomes', label: 'Sample Outcomes' },
  { id: 'process', label: 'Token Process' },
  { id: 'register-token', label: 'RegisterToken API', children: [
    { id: 'rt-making-request', label: 'Making The Request' },
    { id: 'rt-fields', label: 'Available Form Fields' },
    { id: 'rt-validation', label: 'Field Validation' },
    { id: 'rt-sample-xml', label: 'Sample XML' },
    { id: 'rt-responses', label: 'Expected Responses' },
    { id: 'rt-request-reference', label: 'Request Reference' },
  ]},
  { id: 'pay-by-token', label: 'PayByToken API', children: [
    { id: 'pbt-making-request', label: 'Making The Request' },
    { id: 'pbt-fields', label: 'Available Form Fields' },
    { id: 'pbt-validation', label: 'Field Validation' },
    { id: 'pbt-sample-xml', label: 'Sample XML' },
    { id: 'pbt-responses', label: 'Expected Responses' },
    { id: 'pbt-request-reference', label: 'Request Reference' },
  ]},
  { id: 'register-pay-by-token', label: 'RegisterPayByToken API', children: [
    { id: 'rpbt-making-request', label: 'Making The Request' },
    { id: 'rpbt-fields', label: 'Available Form Fields' },
    { id: 'rpbt-validation', label: 'Field Validation' },
    { id: 'rpbt-sample-xml', label: 'Sample XML' },
    { id: 'rpbt-responses', label: 'Expected Responses' },
    { id: 'rpbt-request-reference', label: 'Request Reference' },
  ]},
];

/* ---------- Code samples ---------- */
const makeUrl = (sig) => `https://staging.ecomm365.com/acqapi/service.ashx?Username=SomeName&Password=SomePassword&MessageID=30dd879c-ee2f-11db-8314-0800200c9a66&APISignature=${sig}&Data=<R>...</R>`;

const phpSample = (sigPretty) => `<?php
function httpPost($url, $params)
{
    $params = urldecode(http_build_query($params));
    $ch = curl_init($url);

    curl_setopt($ch, CURLOPT_POST, 1);
    curl_setopt($ch, CURLOPT_POSTFIELDS, $params);
    curl_setopt($ch, CURLOPT_FOLLOWLOCATION, 1);
    curl_setopt($ch, CURLOPT_HEADER, 0);
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, 1);

    $response = curl_exec($ch);
    curl_close($ch);
    return $response;
}

$APIURL = "https://staging.ecomm365.com/acqapi/service.ashx";
$params = array(
    "APISignature" => "${sigPretty}",
    "MessageID"    => GUID(),
    "Username"     => "SomeName",
    "Password"     => "SomePassword",
    "Data"         => "<R>...</R>"
);

$Response = httpPost($APIURL, $params);
$XMLDataArray = simplexml_load_string($Response);

$ResponseR1 = (string)$XMLDataArray->R1;
$ResponseR2 = (string)$XMLDataArray->R2;
//... etc
?>`;

const csharpSample = (sigPretty) => `[HttpPost]
public IActionResult index()
{
    HttpWebRequest httpWReq = (HttpWebRequest)WebRequest.Create("https://staging.ecomm365.com/acqapi/service.ashx");
    UTF8Encoding encoding = new UTF8Encoding();

    string pData = "username=SomeName";
    pData += "&password=SomePassword";
    pData += "&messageId=" + System.Guid.NewGuid().ToString();
    pData += "&ApiSignature=${sigPretty}";
    pData += "&Data=" + WebUtility.UrlEncode(@"<R>...</R>");

    byte[] data = encoding.GetBytes(pData);
    httpWReq.Method = "POST";
    httpWReq.ContentType = "application/x-www-form-urlencoded";
    httpWReq.ContentLength = data.Length;

    byte[] byteArray = Encoding.UTF8.GetBytes(pData);
    Stream dataStream = httpWReq.GetRequestStream();
    dataStream.Write(byteArray, 0, byteArray.Length);
    dataStream.Close();

    HttpWebResponse response = (HttpWebResponse)httpWReq.GetResponse();
    string responseString = new StreamReader(response.GetResponseStream()).ReadToEnd();

    XmlDocument xDoc = new XmlDocument();
    xDoc.LoadXml(responseString);

    XmlNodeList ResponseR1 = xDoc.GetElementsByTagName("R1");
    XmlNodeList ResponseR2 = xDoc.GetElementsByTagName("R2");
    //... etc

    return View();
}`;

/* ---------- RegisterToken ---------- */
const RT_XML = `<!-- RegisterToken Request Demonstration -->
<R>
    <R1>0074F9EF-BF5C-4A46-AF62-3994AC0F12BA</R1>
    <R2>Billing First Name</R2>
    <R3>Billing Middle Name</R3>
    <R4>Billing Last Name</R4>
    <R5>IDA Business and Technology Park</R5>
    <R6>Johnstown</R6>
    <R7>Navan</R7>
    <R8>Billing State or Province</R8>
    <R9>IE</R9>
    <R10>Meath</R10>
</R>`;

const RT_RESP_0060 = `<!-- Sample XML Document -->
<R>
    <R1>0060</R1>
    <R2>Token Registered</R2>
</R>`;

const RT_FIELDS = [
  { name: 'R1',  desc: 'Token — the merchant should provide a unique token for each transaction.', req: 'Y', def: 'AN(50)' },
  { name: 'R2',  desc: 'Billing First Name.', req: 'Y', def: 'AN(50)' },
  { name: 'R3',  desc: 'Billing Middle Name.', req: 'N', def: 'AN(50)' },
  { name: 'R4',  desc: 'Billing Last Name.', req: 'Y', def: 'AN(50)' },
  { name: 'R5',  desc: 'Billing Address Line 1.', req: 'Y', def: 'AN(50)' },
  { name: 'R6',  desc: 'Billing Address Line 2.', req: 'N', def: 'AN(50)' },
  { name: 'R7',  desc: 'Billing City.', req: 'Y', def: 'AN(50)' },
  { name: 'R8',  desc: 'Billing State/Province.', req: 'N', def: 'AN(50)' },
  { name: 'R9',  desc: 'Billing Country Code (ISO 3166). E.g. Ireland = [IE], United Kingdom = [UK].', req: 'Y', def: 'AN(3)' },
  { name: 'R10', desc: 'Billing Postal Code.', req: 'Y', def: 'AN(10)' },
];

const RT_VALIDATION = [
  { name: 'R1',  desc: 'Token.',                    regex: '^[-_0-9A-Za-z]{0,40}$' },
  { name: 'R2',  desc: 'Billing First Name.',       regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R3',  desc: 'Billing Middle Name.',      regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R4',  desc: 'Billing Last Name.',        regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R5',  desc: 'Billing Address Line 1.',   regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R6',  desc: 'Billing Address Line 2.',   regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R7',  desc: 'Billing City.',             regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R8',  desc: 'Billing State/Province.',   regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R9',  desc: 'Billing Country Code.',     regex: '^[a-zA-Z]{2,3}$' },
  { name: 'R10', desc: 'Billing Postal Code.',      regex: "^[a-zA-Z0-9 '.,-]{1,10}$" },
];

/* ---------- PayByToken ---------- */
const PBT_XML = `<!-- PayByToken Request Demonstration -->
<R>
    <R1>0074F9EF-BF5C-4A46-AF62-3994AC0F12BA</R1>
    <R2>1000596</R2>
    <R3>500</R3>
    <R4>http://domain.com/HostedPaymentsPage/Success/</R4>
    <R5>http://domain.com/HostedPaymentsPage/Failure/</R5>
    <R6>6f74f6f2-3537-49d9-b009-8ddb2590f903</R6>
    <udf>
        <I>eCOMM</I>
        <I>435</I>
    </udf>
</R>`;

const PBT_RESP_0061 = `<!-- Sample XML Document -->
<R>
    <R1>0061</R1>
    <R2>Pay Token Received</R2>
    <R3>https://www.somewebsite.com/acs?id=43DAAL4B-78CF-4000-AC6B-EBA1F1116229</R3>
</R>`;

const PBT_NOTIF = `<!-- Sample Confirmation Notification, XML Document -->
<?xml version="1.0"?>
<R>
    <R1>0000</R1>
    <R2>Successful</R2>
    <R3>6f74f6f2-3537-49d9-b009-8ddb2590f903</R3>
    <R4>Successful</R4>
    <R5>Data Not Checked</R5>
    <R8>Match</R8>
    <R9>Ok</R9>
    <R10>Confirm</R10>
    <TKN>0074F9EF-BF5C-4A46-AF62-3994AC0F12BA</TKN>
    <TKNCN>5424*********0819</TKNCN>
    <TKNCardType>VSD</TKNCardType>
    <TKNCardExpMM>12</TKNCardExpMM>
    <TKNCardExpYYYY>2018</TKNCardExpYYYY>
    <udf>
        <I>eCOMM</I>
        <I>435</I>
    </udf>
</R>`;

const PBT_FIELDS = [
  { name: 'R1',  desc: 'Registered Token — the merchant\'s own unique token which was registered using the RegisterToken API.', req: 'Y', def: 'AN(50)' },
  { name: 'R2',  desc: 'Vendor ID.', req: 'Y', def: 'AN(50)' },
  { name: 'R3',  desc: 'Total Transaction Amount (without decimals). E.g. €100.00 = [10000], €0.99 = [99].', req: 'Y', def: 'AN(50)' },
  { name: 'R4',  desc: 'Successful URL — redirects the consumer if the transaction is successful.', req: 'Y', def: 'AN(250)' },
  { name: 'R5',  desc: 'Non-Successful URL — redirects the consumer if the transaction is not successful.', req: 'Y', def: 'AN(250)' },
  { name: 'R6',  desc: 'Transaction Code — unique Transaction Code for each transaction.', req: 'Y', def: 'AN(40)' },
  { name: 'R7',  desc: 'Language Code. E.g. English = [EN], French = [FR], Dutch = [NL].', req: 'N', def: 'AN(2)' },
  { name: 'udf', desc: 'UDF List — user-defined fields.', req: 'N', def: 'AN(20)' },
];

const PBT_VALIDATION = [
  { name: 'R1', desc: 'Registered Token.',                     regex: '^[-_0-9A-Za-z]{0,40}$' },
  { name: 'R2', desc: 'Vendor ID.',                            regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R3', desc: 'Total Transaction Amount.',             regex: '^[0-9]{0,20}$' },
  { name: 'R4', desc: 'Successful URL.',                       regex: '^(ht|f)tp(s?)://…$' },
  { name: 'R5', desc: 'Non-Successful URL.',                   regex: '^(ht|f)tp(s?)://…$' },
  { name: 'R6', desc: 'Transaction Code.',                     regex: '^[-_0-9A-Za-z]{0,40}$' },
  { name: 'R7', desc: 'Language Code.',                        regex: 'EN | FR | NL' },
];

/* ---------- RegisterPayByToken ---------- */
const RPBT_XML = `<!-- RegisterPayByToken Request Demonstration -->
<R>
    <R1>0074F9EF-BF5C-4A46-AF62-3994AC0F12BA</R1>
    <R2>Billing First Name</R2>
    <R3>Billing Middle Name</R3>
    <R4>Billing Last Name</R4>
    <R5>IDA Business and Technology Park</R5>
    <R6>Johnstown</R6>
    <R7>Navan</R7>
    <R8>Billing State or Province</R8>
    <R9>IE</R9>
    <R10>Meath</R10>
    <R11>1000596</R11>
    <R12>500</R12>
    <R13>http://domain.com/HostedPaymentsPage/Success/</R13>
    <R14>http://domain.com/HostedPaymentsPage/Failure/</R14>
    <R15>6f74f6f2-3537-49d9-b009-8ddb2590f903</R15>
</R>`;

const RPBT_FIELDS = [
  { name: 'R1',  desc: 'Token — unique token for each transaction.', req: 'Y', def: 'AN(50)' },
  { name: 'R2',  desc: 'Billing First Name.', req: 'Y', def: 'AN(50)' },
  { name: 'R3',  desc: 'Billing Middle Name.', req: 'N', def: 'AN(50)' },
  { name: 'R4',  desc: 'Billing Last Name.', req: 'Y', def: 'AN(50)' },
  { name: 'R5',  desc: 'Billing Address Line 1.', req: 'Y', def: 'AN(50)' },
  { name: 'R6',  desc: 'Billing Address Line 2.', req: 'N', def: 'AN(50)' },
  { name: 'R7',  desc: 'Billing City.', req: 'Y', def: 'AN(50)' },
  { name: 'R8',  desc: 'Billing State/Province.', req: 'N', def: 'AN(50)' },
  { name: 'R9',  desc: 'Billing Country Code (ISO 3166). E.g. Ireland = [IE], United Kingdom = [UK].', req: 'Y', def: 'AN(3)' },
  { name: 'R10', desc: 'Billing Postal Code.', req: 'Y', def: 'AN(10)' },
  { name: 'R11', desc: 'Vendor ID.', req: 'Y', def: 'AN(50)' },
  { name: 'R12', desc: 'Total Transaction Amount (without decimals). E.g. €100.00 = [10000].', req: 'Y', def: 'AN(50)' },
  { name: 'R13', desc: 'Successful URL.', req: 'Y', def: 'AN(250)' },
  { name: 'R14', desc: 'Non-Successful URL.', req: 'Y', def: 'AN(250)' },
  { name: 'R15', desc: 'Transaction Code — unique Transaction Code for each transaction.', req: 'Y', def: 'AN(40)' },
  { name: 'R16', desc: 'Language Code. E.g. English = [EN], French = [FR], Dutch = [NL].', req: 'N', def: 'AN(2)' },
  { name: 'udf', desc: 'UDF List — user-defined fields.', req: 'N', def: 'AN(20)' },
];

const RPBT_VALIDATION = [
  { name: 'R1',  desc: 'Token.',                      regex: '^[-_0-9A-Za-z]{0,40}$' },
  { name: 'R2',  desc: 'Billing First Name.',         regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R3',  desc: 'Billing Middle Name.',        regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R4',  desc: 'Billing Last Name.',          regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R5',  desc: 'Billing Address Line 1.',     regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R6',  desc: 'Billing Address Line 2.',     regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R7',  desc: 'Billing City.',               regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R8',  desc: 'Billing State/Province.',     regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R9',  desc: 'Billing Country Code.',       regex: '^[a-zA-Z]{2,3}$' },
  { name: 'R10', desc: 'Billing Postal Code.',        regex: "^[a-zA-Z0-9 '.,-]{1,10}$" },
  { name: 'R11', desc: 'Vendor ID.',                  regex: "^[a-zA-Z0-9 ,'._-]{0,50}$" },
  { name: 'R12', desc: 'Total Transaction Amount.',   regex: '^[0-9]{0,20}$' },
  { name: 'R13', desc: 'Successful URL.',             regex: '^(ht|f)tp(s?)://…$' },
  { name: 'R14', desc: 'Non-Successful URL.',         regex: '^(ht|f)tp(s?)://…$' },
  { name: 'R15', desc: 'Transaction Code.',           regex: '^[-_0-9A-Za-z]{0,40}$' },
  { name: 'R16', desc: 'Language Code.',              regex: 'EN | FR | NL' },
];

/* ---------- Shared response field tables ---------- */
const SERVER_RESP_TOKEN_ONLY = [
  { name: 'R1', desc: (<>Error Code (see <Link to="/technical-documentation/appendix/error-codes" className="text-[#1F4E79] underline hover:text-[#163a5f] font-semibold">Error Codes</Link>).</>), def: 'AN(50)' },
  { name: 'R2', desc: 'Description.', def: 'AN(50)' },
];

const SERVER_RESP_WITH_URL = [
  { name: 'R1', desc: (<>Error Code (see <Link to="/technical-documentation/appendix/error-codes" className="text-[#1F4E79] underline hover:text-[#163a5f] font-semibold">Error Codes</Link>).</>), def: 'AN(50)' },
  { name: 'R2', desc: 'Description.', def: 'AN(50)' },
  { name: 'R3', desc: 'The Hosted Payments Page URL to complete the transaction (used for the iFrame).', def: 'AN(2048)' },
];

const CONFIRMATION_FIELDS = [
  { name: 'R1',  desc: (<>Error Code (see <Link to="/technical-documentation/appendix/error-codes" className="text-[#1F4E79] underline hover:text-[#163a5f] font-semibold">Error Codes</Link>).</>), def: 'N(4)' },
  { name: 'R2',  desc: 'Description.', def: 'AN(250)' },
  { name: 'R3',  desc: "Merchant's own reference code to the transaction.", def: 'AN(40)' },
  { name: 'R4',  desc: 'The transaction status.', def: 'AN(20)' },
  { name: 'R5',  desc: 'Response from AVS and CV2 checks (All Match / Security Code Only / Address Only / No Data / Data Not Checked).', def: 'AN(50)' },
  { name: 'R6',  desc: "Cardholder address numeric check (Match / Not Match).", def: 'AN(20)' },
  { name: 'R7',  desc: 'Cardholder Postcode check (Match / Not Match).', def: 'AN(20)' },
  { name: 'R8',  desc: 'Cardholder CV2 check (Match / Not Match).', def: 'AN(20)' },
  { name: 'R9',  desc: '3D Secure checks (OK / Error).', def: 'AN(50)' },
  { name: 'R10', desc: 'Confirmation Type (Confirm / Authorisation).', def: 'AN(10)' },
  { name: 'R11', desc: 'Merchant specified data returned on the response.', def: 'AN(20)' },
  { name: 'R12', desc: 'Merchant specified transaction identifier returned on the response.', def: 'AN(20)' },
  { name: 'TKN',          desc: 'Token used.', def: 'AN(50)' },
  { name: 'TKNCN',        desc: 'Card Number saved to the token (masked).', def: 'AN(16)' },
  { name: 'TKNCardType',  desc: 'Card Type saved to the token.', def: 'AN(5)' },
  { name: 'TKNCardExpMM', desc: 'Card Expiry Month saved to the token.', def: 'N(2)' },
  { name: 'TKNCardExpYYYY', desc: 'Card Expiry Year saved to the token.', def: 'N(4)' },
  { name: 'udf', desc: 'UDF List — any merchant-supplied UDF values returned back.', def: 'AN(50)' },
];

/* Local LangTabs removed — now using shared <LangTabs /> from /components/LangTabs.jsx */

/* ---------- Reusable field / validation / response field tables ---------- */
const FieldsTable = ({ data, testid }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm border-collapse" data-testid={testid}>
      <thead>
        <tr className="bg-[#1F4E79] text-white">
          <th className="text-left p-3 w-20">Field</th>
          <th className="text-left p-3">Description</th>
          <th className="text-left p-3 w-24">Required</th>
          <th className="text-left p-3 w-28">Definition</th>
        </tr>
      </thead>
      <tbody>
        {data.map((f, i) => (
          <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
            <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
            <td className="p-3">{f.desc}</td>
            <td className="p-3"><ReqBadge value={f.req} /></td>
            <td className="p-3 font-mono text-xs">{f.def}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ValidationTable = ({ data }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="bg-[#1F4E79] text-white">
          <th className="text-left p-3 w-20">Field</th>
          <th className="text-left p-3">Description</th>
          <th className="text-left p-3">Validation (Regex)</th>
        </tr>
      </thead>
      <tbody>
        {data.map((v, i) => (
          <tr key={v.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
            <td className="p-3 font-mono font-semibold text-[#1F4E79]">{v.name}</td>
            <td className="p-3">{v.desc}</td>
            <td className="p-3 font-mono text-xs break-all">{v.regex}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const RespFieldsTable = ({ data }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="bg-[#1F4E79] text-white">
          <th className="text-left p-3 w-40">Field</th>
          <th className="text-left p-3">Description</th>
          <th className="text-left p-3 w-28">Definition</th>
        </tr>
      </thead>
      <tbody>
        {data.map((f, i) => (
          <tr key={f.name} className={`border-b border-gray-200 ${i % 2 ? 'bg-gray-50/50' : ''}`}>
            <td className="p-3 font-mono font-semibold text-[#1F4E79]">{f.name}</td>
            <td className="p-3">{f.desc}</td>
            <td className="p-3 font-mono text-xs">{f.def}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ---------- Tab switcher (top of page) ---------- */
const ApiSwitcher = () => {
  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };
  const apis = [
    { id: 'register-token', label: 'RegisterToken', tag: 'Step 1' },
    { id: 'pay-by-token', label: 'PayByToken', tag: 'Process' },
    { id: 'register-pay-by-token', label: 'RegisterPayByToken', tag: 'All-in-one' },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6" data-testid="api-switcher">
      {apis.map((a) => (
        <a
          key={a.id}
          href={`#${a.id}`}
          onClick={scrollTo(a.id)}
          className="group rounded-lg border-2 border-[#1F4E79]/15 bg-gradient-to-br from-white to-blue-50/40 p-4 hover:border-[#1F4E79] hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
          data-testid={`api-switch-${a.id}`}
        >
          <div className="flex items-center gap-2 mb-1">
            <KeyRound size={15} className="text-[#1F4E79]" />
            <span className="text-[10px] font-bold text-[#ef5b25] uppercase tracking-wider">{a.tag}</span>
          </div>
          <div className="font-bold text-[#1F4E79] group-hover:text-[#163a5f] text-base">{a.label}</div>
          <div className="text-xs text-gray-500 mt-0.5">Jump to section &rarr;</div>
        </a>
      ))}
    </div>
  );
};

/* ========================================================== */
const HostedPaymentsPageToken = () => (
  <ContainerPageLayout
    currentId="hosted-payment-token"
    title="Hosted Payments Page (Token)"
    subtitle="Tokenised iFrame checkout — register a token, then charge returning customers with only the CVV required. Includes RegisterToken, PayByToken and RegisterPayByToken APIs."
    titleBadge="Token"
    signature={["registertoken", "paybytoken", "registerpaybytoken"]}
    toc={TOC}
    mainTestId="hosted-payment-token-page"
    sidebarTestId="hosted-payment-token-sidebar"
  >
    {/* Overview */}
    <section id="overview" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Overview</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        This solution allows the merchant to have an embedded iFrame within their website that will load eCOMM's Hosted Payments page. The merchant's developer can choose to embed the iFrame within a checkout page or create a pop-up modal that will contain the iFrame.
      </p>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        The consumer's data is collected on the merchant's website where it is posted to either the{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">RegisterToken</code> or{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">RegisterPayByToken</code> API. The response from{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">RegisterPayByToken</code> or{' '}
        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">PayByToken</code> will contain the URL which is loaded into the iFrame. The consumer enters their card details; if the card is{' '}
        <Link to="/technical-documentation/api-reference/3d-secure-auth" className="text-[#1F4E79] underline hover:text-[#163a5f]">3D Secure</Link>, they are prompted to authenticate. <strong>Our Hosted Payments Page handles the flow of 3D Secure.</strong>
      </p>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        <strong>Card details are only required the first time.</strong> For future transactions, the cardholder's CVV is the only thing they need to enter. This section lists our three Token APIs:
      </p>
      <ol className="list-decimal pl-6 space-y-2 text-gray-700 text-sm leading-relaxed">
        <li><strong>RegisterToken</strong> — Allows the merchant to generate and register a unique token. This token will be used with <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">PayByToken</code> to process transactions.</li>
        <li><strong>PayByToken</strong> — Process transactions by supplying the generated token received from <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">RegisterToken</code> or <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">RegisterPayByToken</code>. The transaction result is sent to the merchant's Notification URL.</li>
        <li><strong>RegisterPayByToken</strong> — Register a new token <em>and</em> process a transaction at the same time. The transaction result is sent to the merchant's Notification URL.</li>
      </ol>

      <div className="bg-amber-50/90 border border-amber-200 rounded-lg p-4 flex items-start gap-3 mt-5">
        <Info size={18} className="text-amber-700 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-gray-700">
          <strong>Notification Method must be set to <em>Both</em>:</strong> please provide eCOMM with your desired Notification URL — for Hosted Payments Page the notification method can only be set to <code className="bg-white/70 px-1 rounded">Both</code>.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
        <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white/70 p-4">
          <ShieldCheck size={20} className="text-[#1F4E79] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-gray-900">Minimal PCI Scope</p>
            <p className="text-xs text-gray-600 mt-1">Card data never touches your servers — entered directly into the iFrame.</p>
          </div>
        </div>
        <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white/70 p-4">
          <Palette size={20} className="text-[#1F4E79] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-gray-900">Custom CSS Styling</p>
            <p className="text-xs text-gray-600 mt-1">Supply eCOMM with a CSS file and we'll brand the Hosted Payments Page to match your site.</p>
          </div>
        </div>
        <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white/70 p-4">
          <Globe size={20} className="text-[#1F4E79] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-gray-900">DCC Ready</p>
            <p className="text-xs text-gray-600 mt-1">Supports Dynamic Currency Conversion — supply eCOMM with the currencies you wish to accept.</p>
          </div>
        </div>
      </div>

      <ApiSwitcher />
    </section>

    {/* Sample Outcomes */}
    <section id="outcomes" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Sample Hosted Payments Page Outcomes</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-4">
        This section demonstrates what the consumer can expect when they load the Hosted Payments Page. These sample screens help merchants answer common questions about the token process.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <figure className="rounded-lg border border-gray-200 overflow-hidden bg-white/70">
          <img src="https://dev.ecomm365.com/ecommerce/assets/images/Pages/Hosted-Payments-Page-Token/HPP.jpg" alt="Sample HPP Form — First Transaction" className="w-full h-auto" loading="lazy" />
          <figcaption className="px-3 py-2 text-xs text-gray-600 border-t border-gray-200 bg-gray-50">
            First transaction — cardholder enters card details; token is saved.
          </figcaption>
        </figure>
        <figure className="rounded-lg border border-gray-200 overflow-hidden bg-white/70">
          <img src="https://dev.ecomm365.com/ecommerce/assets/images/Pages/Hosted-Payments-Page-Token/HPP-2.jpg" alt="Sample HPP Form — Future Transaction" className="w-full h-auto" loading="lazy" />
          <figcaption className="px-3 py-2 text-xs text-gray-600 border-t border-gray-200 bg-gray-50">
            Future transaction — only CVV required; other fields are auto-filled.
          </figcaption>
        </figure>
        <figure className="rounded-lg border border-gray-200 overflow-hidden bg-white/70">
          <img src="https://dev.ecomm365.com/ecommerce/assets/images/Pages/Hosted-Payments-Page-Token/HPP-DCC.jpg" alt="Sample HPP (DCC) Form — First Transaction" className="w-full h-auto" loading="lazy" />
          <figcaption className="px-3 py-2 text-xs text-gray-600 border-t border-gray-200 bg-gray-50">
            First transaction with DCC — merchant enters card details; DCC option displayed.
          </figcaption>
        </figure>
        <figure className="rounded-lg border border-gray-200 overflow-hidden bg-white/70">
          <img src="https://dev.ecomm365.com/ecommerce/assets/images/Pages/Hosted-Payments-Page-Token/HPP-DCC-2.jpg" alt="Sample HPP (DCC) Form — Future Transaction" className="w-full h-auto" loading="lazy" />
          <figcaption className="px-3 py-2 text-xs text-gray-600 border-t border-gray-200 bg-gray-50">
            Future transaction with DCC — only CVV required; DCC option displayed.
          </figcaption>
        </figure>
      </div>
    </section>

    {/* Process */}
    <section id="process" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Hosted Payments Page (Token) Process</h2>
      <p className="text-gray-700 text-base leading-relaxed mb-6">
        The process is similar to the standard Hosted Payments Page, however the token solution allows merchants to save consumer card information in a token to speed up future transactions — once the token is used, the Hosted Payments Page will already have most of the card information auto-filled.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-lg border-2 border-[#1F4E79]/15 bg-gradient-to-br from-white to-blue-50/40 p-5">
          <div className="text-[10px] font-bold text-[#ef5b25] uppercase tracking-wider mb-1">Step 1 · Register</div>
          <h3 className="font-bold text-[#1F4E79] text-base mb-2">RegisterToken</h3>
          <ol className="list-decimal pl-4 text-sm text-gray-700 space-y-2 leading-relaxed">
            <li>Send a request to <code className="bg-gray-100 px-1 py-0.5 rounded text-xs font-mono">RegisterToken</code> with consumer information and a unique token. The response returns <strong className="text-[#c32222]">0060 — Token Registered</strong>.</li>
            <li>Supply the generated token when using <code className="bg-gray-100 px-1 py-0.5 rounded text-xs font-mono">PayByToken</code> to process future transactions.</li>
          </ol>
        </div>
        <div className="rounded-lg border-2 border-[#1F4E79]/15 bg-gradient-to-br from-white to-blue-50/40 p-5">
          <div className="text-[10px] font-bold text-[#ef5b25] uppercase tracking-wider mb-1">Step 2 · Charge</div>
          <h3 className="font-bold text-[#1F4E79] text-base mb-2">PayByToken</h3>
          <ol className="list-decimal pl-4 text-sm text-gray-700 space-y-2 leading-relaxed">
            <li>Send a request to <code className="bg-gray-100 px-1 py-0.5 rounded text-xs font-mono">PayByToken</code> providing the token registered earlier and transaction details.</li>
            <li>On success, the response returns <strong className="text-[#c32222]">0061 — Pay Token Received</strong> including the Hosted Payments Page URL to load inside the iFrame.</li>
          </ol>
        </div>
        <div className="rounded-lg border-2 border-[#1F4E79]/15 bg-gradient-to-br from-white to-blue-50/40 p-5">
          <div className="text-[10px] font-bold text-[#ef5b25] uppercase tracking-wider mb-1">All-in-one</div>
          <h3 className="font-bold text-[#1F4E79] text-base mb-2">RegisterPayByToken</h3>
          <ol className="list-decimal pl-4 text-sm text-gray-700 space-y-2 leading-relaxed">
            <li>Send a request to <code className="bg-gray-100 px-1 py-0.5 rounded text-xs font-mono">RegisterPayByToken</code> providing consumer information and a unique token.</li>
            <li>On success, the response returns <strong className="text-[#c32222]">0061 — Pay Token Received</strong> including the Hosted Payments Page URL — register and process in one call.</li>
          </ol>
        </div>
      </div>
    </section>

    {/* ================= RegisterToken API ================= */}
    <section id="register-token" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">RegisterToken API</h2>
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-[#ef5b25] text-white uppercase tracking-wide">Step 1</span>
      </div>
      <p className="text-gray-700 text-base leading-relaxed">
        Register a unique token tied to a consumer's billing information. Once registered, the token can be re-used with <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">PayByToken</code> to charge the consumer with minimum friction.
      </p>

      <div id="rt-making-request" className="scroll-mt-28 mt-8">
        <h3 className="text-xl font-bold text-gray-900 mb-3">Making The Request</h3>
        <p className="text-gray-700 text-sm leading-relaxed mb-3">
          The request string for the <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono">RegisterToken</code> call must be composed of the following information:
        </p>
        <ol className="list-decimal pl-6 space-y-1 text-gray-700 text-sm mb-5">
          <li><strong>Username</strong> = SomeName</li>
          <li><strong>Password</strong> = SomePassword</li>
          <li><strong>MessageID</strong> = <em>*GUID</em></li>
          <li><strong>APISignature</strong> = RegisterToken</li>
          <li><strong>Data</strong> = Form data in XML format</li>
        </ol>
        <CodeBlock code={makeUrl('registertoken')} lang="http" testid="rt-url-code" />

        <div className="flex flex-wrap items-center justify-between gap-3 mt-6 mb-3">
          <h4 className="text-lg font-bold text-gray-900">Sample <code className="bg-gray-100 px-2 py-0.5 rounded text-base font-mono">RegisterToken</code> Request</h4>
          <DownloadButtons />
        </div>
        <LangTabs samples={{ php: phpSample("RegisterToken"), csharp: csharpSample("RegisterToken") }} testidBase="rt" />
      </div>

      <div id="rt-fields" className="scroll-mt-28 mt-8">
        <h3 className="text-xl font-bold text-gray-900 mb-3">Available Form Data Fields</h3>
        <FieldsTable data={RT_FIELDS} testid="rt-fields-table" />
      </div>

      <div id="rt-validation" className="scroll-mt-28 mt-8">
        <h3 className="text-xl font-bold text-gray-900 mb-3">Available Form Data Fields — Validation</h3>
        <ValidationTable data={RT_VALIDATION} />
      </div>

      <div id="rt-sample-xml" className="scroll-mt-28 mt-8">
        <h3 className="text-xl font-bold text-gray-900 mb-3">Sample <code className="bg-gray-100 px-2 py-0.5 rounded text-base font-mono">RegisterToken</code> XML Document</h3>
        <CodeBlock code={RT_XML} lang="xml" testid="rt-xml-code" />
      </div>

      <div id="rt-responses" className="scroll-mt-28 mt-8">
        <h3 className="text-xl font-bold text-gray-900 mb-3">Expected XML Documents</h3>
        <p className="text-gray-700 text-sm leading-relaxed mb-4">Data returned from the request:</p>
        <RespFieldsTable data={SERVER_RESP_TOKEN_ONLY} />

        <div className="bg-blue-50/80 border border-blue-200 rounded-lg p-5 my-6">
          <p className="font-semibold text-[#1F4E79] mb-3">Expected error code responses:</p>
          <ol className="list-decimal pl-5 text-sm text-gray-700 space-y-2">
            <li><strong className="text-[#c32222]">0060</strong> — Token Registered. The token is now registered; you may use it with <code className="bg-white/70 px-1 rounded">PayByToken</code>.</li>
          </ol>
        </div>

        <h4 className="text-lg font-bold text-gray-900 mb-2">Sample XML Response (0060)</h4>
        <CodeBlock code={RT_RESP_0060} lang="xml" testid="rt-resp-0060-code" />
      </div>

      <div id="rt-request-reference" className="scroll-mt-28 mt-10">
        <ApiPlayground
          apiSignature="registertoken"
          title="Request Reference — RegisterToken"
          description="Register a new token. A successful response returns 0060 — Token Registered."
          defaultData={RT_XML.replace('<!-- RegisterToken Request Demonstration -->\n', '')}
        />
      </div>
    </section>

    {/* ================= PayByToken API ================= */}
    <section id="pay-by-token" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">PayByToken API</h2>
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-[#1F4E79] text-white uppercase tracking-wide">Process</span>
      </div>
      <p className="text-gray-700 text-base leading-relaxed">
        Charge a previously registered token. The response includes the Hosted Payments Page URL to load into your iFrame — the cardholder typically only needs to enter their CVV.
      </p>

      <div id="pbt-making-request" className="scroll-mt-28 mt-8">
        <h3 className="text-xl font-bold text-gray-900 mb-3">Making The Request</h3>
        <ol className="list-decimal pl-6 space-y-1 text-gray-700 text-sm mb-5">
          <li><strong>Username</strong> = SomeName</li>
          <li><strong>Password</strong> = SomePassword</li>
          <li><strong>MessageID</strong> = <em>*GUID</em></li>
          <li><strong>APISignature</strong> = PayByToken</li>
          <li><strong>Data</strong> = Form data in XML format</li>
        </ol>
        <CodeBlock code={makeUrl('paybytoken')} lang="http" testid="pbt-url-code" />

        <div className="flex flex-wrap items-center justify-between gap-3 mt-6 mb-3">
          <h4 className="text-lg font-bold text-gray-900">Sample <code className="bg-gray-100 px-2 py-0.5 rounded text-base font-mono">PayByToken</code> Request</h4>
          <DownloadButtons />
        </div>
        <LangTabs samples={{ php: phpSample("PayByToken"), csharp: csharpSample("PayByToken") }} testidBase="pbt" />
      </div>

      <div id="pbt-fields" className="scroll-mt-28 mt-8">
        <h3 className="text-xl font-bold text-gray-900 mb-3">Available Form Data Fields</h3>
        <FieldsTable data={PBT_FIELDS} testid="pbt-fields-table" />
      </div>

      <div id="pbt-validation" className="scroll-mt-28 mt-8">
        <h3 className="text-xl font-bold text-gray-900 mb-3">Available Form Data Fields — Validation</h3>
        <ValidationTable data={PBT_VALIDATION} />
      </div>

      <div id="pbt-sample-xml" className="scroll-mt-28 mt-8">
        <h3 className="text-xl font-bold text-gray-900 mb-3">Sample <code className="bg-gray-100 px-2 py-0.5 rounded text-base font-mono">PayByToken</code> XML Document</h3>
        <CodeBlock code={PBT_XML} lang="xml" testid="pbt-xml-code" />
      </div>

      <div id="pbt-responses" className="scroll-mt-28 mt-8">
        <h3 className="text-xl font-bold text-gray-900 mb-3">Expected XML Documents</h3>

        <h4 className="text-base font-bold text-gray-900 mb-2">Server Response</h4>
        <RespFieldsTable data={SERVER_RESP_WITH_URL} />

        <h4 className="text-base font-bold text-gray-900 mb-2 mt-6">Confirmation Notification</h4>
        <p className="text-xs text-gray-600 mb-3">Sent to your Notification URL once the transaction completes on the Hosted Payments Page.</p>
        <RespFieldsTable data={CONFIRMATION_FIELDS} />

        <div className="bg-blue-50/80 border border-blue-200 rounded-lg p-5 my-6">
          <p className="font-semibold text-[#1F4E79] mb-3">Expected error code responses:</p>
          <ol className="list-decimal pl-5 text-sm text-gray-700 space-y-2">
            <li><strong className="text-[#c32222]">0061</strong> — Pay Token Received. The response includes the URL required for the iFrame in the <code className="bg-white/70 px-1 rounded">&lt;R3&gt;</code> field.</li>
          </ol>
        </div>

        <h4 className="text-lg font-bold text-gray-900 mb-2">Sample XML Response (0061)</h4>
        <CodeBlock code={PBT_RESP_0061} lang="xml" testid="pbt-resp-0061-code" />

        <h4 className="text-lg font-bold text-gray-900 mb-2 mt-6">Sample XML Response (Confirmation Notification)</h4>
        <p className="text-sm text-gray-600 mb-2">Expected to return to the Notification URL if your notification method is set up to do so.</p>
        <CodeBlock code={PBT_NOTIF} lang="xml" testid="pbt-resp-notif-code" />
      </div>

      <div id="pbt-request-reference" className="scroll-mt-28 mt-10">
        <ApiPlayground
          apiSignature="paybytoken"
          title="Request Reference — PayByToken"
          description="Charge a previously registered token. The R3 field in the response contains the iFrame URL."
          defaultData={PBT_XML.replace('<!-- PayByToken Request Demonstration -->\n', '')}
        />
      </div>
    </section>

    {/* ================= RegisterPayByToken API ================= */}
    <section id="register-pay-by-token" className="scroll-mt-28 bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-10 mb-6">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">RegisterPayByToken API</h2>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold bg-[#ef5b25] text-white uppercase tracking-wide">
          <Sparkles size={11} />
          All-in-one
        </span>
      </div>
      <p className="text-gray-700 text-base leading-relaxed">
        Register a token <em>and</em> charge it in a single call — ideal for first-time checkouts where you want to store the card for future frictionless payments.
      </p>

      <div id="rpbt-making-request" className="scroll-mt-28 mt-8">
        <h3 className="text-xl font-bold text-gray-900 mb-3">Making The Request</h3>
        <ol className="list-decimal pl-6 space-y-1 text-gray-700 text-sm mb-5">
          <li><strong>Username</strong> = SomeName</li>
          <li><strong>Password</strong> = SomePassword</li>
          <li><strong>MessageID</strong> = <em>*GUID</em></li>
          <li><strong>APISignature</strong> = RegisterPayByToken</li>
          <li><strong>Data</strong> = Form data in XML format</li>
        </ol>
        <CodeBlock code={makeUrl('registerpaybytoken')} lang="http" testid="rpbt-url-code" />

        <div className="flex flex-wrap items-center justify-between gap-3 mt-6 mb-3">
          <h4 className="text-lg font-bold text-gray-900">Sample <code className="bg-gray-100 px-2 py-0.5 rounded text-base font-mono">RegisterPayByToken</code> Request</h4>
          <DownloadButtons />
        </div>
        <LangTabs samples={{ php: phpSample("RegisterPayByToken"), csharp: csharpSample("RegisterPayByToken") }} testidBase="rpbt" />
      </div>

      <div id="rpbt-fields" className="scroll-mt-28 mt-8">
        <h3 className="text-xl font-bold text-gray-900 mb-3">Available Form Data Fields</h3>
        <FieldsTable data={RPBT_FIELDS} testid="rpbt-fields-table" />
      </div>

      <div id="rpbt-validation" className="scroll-mt-28 mt-8">
        <h3 className="text-xl font-bold text-gray-900 mb-3">Available Form Data Fields — Validation</h3>
        <ValidationTable data={RPBT_VALIDATION} />
      </div>

      <div id="rpbt-sample-xml" className="scroll-mt-28 mt-8">
        <h3 className="text-xl font-bold text-gray-900 mb-3">Sample <code className="bg-gray-100 px-2 py-0.5 rounded text-base font-mono">RegisterPayByToken</code> XML Document</h3>
        <CodeBlock code={RPBT_XML} lang="xml" testid="rpbt-xml-code" />
      </div>

      <div id="rpbt-responses" className="scroll-mt-28 mt-8">
        <h3 className="text-xl font-bold text-gray-900 mb-3">Expected XML Documents</h3>

        <h4 className="text-base font-bold text-gray-900 mb-2">Server Response</h4>
        <RespFieldsTable data={SERVER_RESP_WITH_URL} />

        <h4 className="text-base font-bold text-gray-900 mb-2 mt-6">Confirmation Notification</h4>
        <p className="text-xs text-gray-600 mb-3">Sent to your Notification URL once the transaction completes on the Hosted Payments Page.</p>
        <RespFieldsTable data={CONFIRMATION_FIELDS} />

        <div className="bg-blue-50/80 border border-blue-200 rounded-lg p-5 my-6">
          <p className="font-semibold text-[#1F4E79] mb-3">Expected error code responses:</p>
          <ol className="list-decimal pl-5 text-sm text-gray-700 space-y-2">
            <li><strong className="text-[#c32222]">0061</strong> — Pay Token Received. The response includes the URL required for the iFrame in the <code className="bg-white/70 px-1 rounded">&lt;R3&gt;</code> field.</li>
          </ol>
        </div>

        <h4 className="text-lg font-bold text-gray-900 mb-2">Sample XML Response (0061)</h4>
        <CodeBlock code={PBT_RESP_0061} lang="xml" testid="rpbt-resp-0061-code" />

        <h4 className="text-lg font-bold text-gray-900 mb-2 mt-6">Sample XML Response (Confirmation Notification)</h4>
        <p className="text-sm text-gray-600 mb-2">Expected to return to the Notification URL if your notification method is set up to do so.</p>
        <CodeBlock code={PBT_NOTIF} lang="xml" testid="rpbt-resp-notif-code" />
      </div>

      <div id="rpbt-request-reference" className="scroll-mt-28 mt-10">
        <ApiPlayground
          apiSignature="registerpaybytoken"
          title="Request Reference — RegisterPayByToken"
          description="Register and charge a token in one call. The R3 field in the response contains the iFrame URL."
          defaultData={RPBT_XML.replace('<!-- RegisterPayByToken Request Demonstration -->\n', '')}
        />
      </div>
    </section>
  </ContainerPageLayout>
);

export default HostedPaymentsPageToken;
